import { parse as parseParams } from 'hub-http/helpers/params';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useLocation } from 'react-router-dom';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { ReviewRatingInput, ReviewTextInput } from 'trellis-ecosystem';
import { EcosystemReviewWizard } from 'marketplace-ui-reviews/components/wizard/EcosystemReviewWizard';
import { PENDING } from 'marketplace-ui-reviews/constants/UnreviewableReason';
import { getIsPublicApp } from '../../selectors/app';
import { AssetReviewWizardHeader } from './AssetReviewWizardHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DETAILED_RATING_AXES = ['USABILITY', 'PERFORMANCE', 'VALUE', 'SUPPORT_DOCS'];
const VALIDATION_SCHEMA = {
  content: {
    TITLE: {
      required: true
    },
    REVIEW: {
      required: true,
      minCharacters: 75,
      maxCharacters: 3000
    },
    CUSTOMIZABLE: {
      required: true
    }
  },
  ratings: {
    OVERALL: {
      required: true
    },
    USABILITY: {
      required: true
    },
    PERFORMANCE: {
      required: true
    },
    VALUE: {
      required: true
    }
  }
};
export function AssetReviewWizard({
  editedReview,
  name,
  onClose,
  offeringId,
  offeringType,
  onSuccess,
  onTrackInteraction,
  providerName,
  reviewType,
  slug,
  supportLink,
  thumbnailUrl,
  unreviewableReason,
  versionNumber
}) {
  const {
    search
  } = useLocation();
  const {
    eco_review_source,
    rating
  } = parseParams(search.slice(1));
  const reviewSource = eco_review_source || 'details';
  const parsedRating = /^[1-5]$/.test(rating) ? parseInt(rating, 10) : undefined;
  const isPublic = useSelector(getIsPublicApp);
  const isEditing = !!editedReview;
  let defaultReview;
  const emptyReview = {
    content: {},
    ratings: {
      OVERALL: parsedRating || 0
    },
    tags: {
      CATALOG_SERVICES: []
    }
  };
  try {
    const storedReview = JSON.parse(sessionStorage.getItem(`${slug}_review`));
    defaultReview = storedReview || emptyReview;
  } catch (__error) {
    defaultReview = emptyReview;
  }
  const [overallRating, setOverallRating] = useState(defaultReview.ratings.OVERALL);
  const [selectedDetailedRatings, setSelectedDetailedRatings] = useState(!!defaultReview.ratings && !!defaultReview.ratings['VALUE'] && !!defaultReview.ratings['USABILITY'] && !!defaultReview.ratings['PERFORMANCE']);
  useEffect(() => {
    onTrackInteraction(isEditing ? 'open edit review wizard' : 'open review wizard', {
      reviewSource,
      section: 'review'
    });
  }, [isEditing, onTrackInteraction, reviewSource]);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}`) {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`,
          options: {
            offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}_lowercase`)
          }
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.details.reviews.unreviewable.titleText"
        }),
        type: 'danger'
      });
    }
  }, [isEditing, offeringType, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction('submit review', {
      reviewSource,
      section: 'review'
    });
    sessionStorage.removeItem(`${slug}_review`);
    onSuccess();
  }, [onSuccess, onTrackInteraction, reviewSource, slug]);
  const handleClose = useCallback(() => {
    onTrackInteraction('dismiss review wizard', {
      reviewSource,
      section: 'review'
    });
    onClose();
  }, [onClose, onTrackInteraction, reviewSource]);
  const onChange = useCallback(review => {
    if (review.ratings['USABILITY'] && review.ratings['PERFORMANCE'] && review.ratings['VALUE']) {
      setSelectedDetailedRatings(true);
    }
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction('set overall rating', {
        section: 'review',
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    if (!isEditing) {
      sessionStorage.setItem(`${slug}_review`, JSON.stringify(review));
    }
  }, [isEditing, onTrackInteraction, overallRating, slug]);
  const WizardHeader = useMemo(() => () => /*#__PURE__*/_jsx(AssetReviewWizardHeader, {
    name: name,
    offeringType: offeringType,
    providerName: providerName,
    thumbnailUrl: thumbnailUrl,
    versionNumber: versionNumber
  }), [name, offeringType, providerName, thumbnailUrl, versionNumber]);
  if (isPublic || !isEditing && unreviewableReason) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "REVIEW_WIZARD_LOAD"
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: /*#__PURE__*/_jsx(EcosystemReviewWizard, {
      defaultReview: defaultReview,
      editedReview: editedReview,
      HeaderComponent: WizardHeader,
      offeringId: offeringId,
      onChange: onChange,
      onClose: handleClose,
      onSuccess: handleSuccess,
      ratingSection: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h5",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.reviews.wizard.header.text"
          })
        }), supportLink && /*#__PURE__*/_jsx(FormattedJSXMessage, {
          elements: {
            Link
          },
          message: "marketplace-assets-ui.assets.reviews.wizard.header.subheader_jsx",
          options: {
            name,
            external: true,
            href: supportLink,
            onClick: () => {
              onTrackInteraction('click support link', {
                section: 'review'
              });
            }
          }
        }), /*#__PURE__*/_jsx(ReviewRatingInput, {
          fieldKey: "OVERALL",
          label: unescapedText('marketplace-assets-ui.assets.reviews.wizard.labels.OVERALL')
        }), (isEditing || !!overallRating) && DETAILED_RATING_AXES.map(axis => /*#__PURE__*/_jsx(ReviewRatingInput, {
          fieldKey: axis,
          label: unescapedText(`marketplace-assets-ui.assets.reviews.wizard.labels.${axis}`),
          tooltip: unescapedText(`marketplace-assets-ui.assets.reviews.wizard.tooltips.${axis}`),
          type: "pill"
        }, axis))]
      }),
      requireEditReason: true,
      reviewType: reviewType,
      validationSchema: VALIDATION_SCHEMA,
      children: (isEditing || !!overallRating && selectedDetailedRatings) && /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(ReviewTextInput, {
          fieldKey: "TITLE",
          label: unescapedText('marketplace-assets-ui.assets.reviews.wizard.labels.TITLE'),
          resize: false,
          rows: 1,
          placeholder: unescapedText('marketplace-assets-ui.assets.reviews.wizard.placeholders.TITLE')
        }), /*#__PURE__*/_jsx(ReviewTextInput, {
          fieldKey: 'REVIEW',
          label: unescapedText(`marketplace-assets-ui.assets.reviews.wizard.labels.REVIEW`),
          placeholder: unescapedText(`marketplace-assets-ui.assets.reviews.wizard.placeholders.REVIEW`, {
            name
          }),
          rows: 4
        }), ['CUSTOMIZABLE', 'PROS', 'CONS'].map(axis => /*#__PURE__*/_jsx(ReviewTextInput, {
          fieldKey: axis,
          label: unescapedText(`marketplace-assets-ui.assets.reviews.wizard.labels.${axis}`),
          placeholder: unescapedText(`marketplace-assets-ui.assets.reviews.wizard.placeholders.${axis}`, {
            name
          })
        }, axis))]
      })
    })
  });
}